import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,

  Divider,
  Paper,
  CircularProgress,
  Link,
  Button
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import EmptyState from './EmptyState';
import RatingLegend from './RatingLegend';
import MatchScoreIndicator from './MatchScoreIndicator';

// Reusable StarRating component for user interaction
const StarRating = ({ rating, setRating, disabled, movieId }) => {
  const [hover, setHover] = useState(0);

  const handleRateMovie = async (newRating) => {
    const user = JSON.parse(localStorage.getItem('user'));

    try {
      await api.rateMovie(user.id, movieId, newRating, 'tmdb');
      setRating(newRating);
    } catch (error) {
      console.error("Failed to save rating:", error);
    }
  };

  return (
    <Box>
      {[...Array(5)].map((star, index) => {
        const ratingValue = index + 1;
        return (
          <Button
            key={ratingValue}
            sx={{ minWidth: 'auto', padding: '2px', color: ratingValue <= (hover || rating) ? 'gold' : 'grey.400' }}
            onClick={() => handleRateMovie(ratingValue === rating ? 0 : ratingValue)}
            onMouseEnter={() => !disabled && setHover(ratingValue)}
            onMouseLeave={() => !disabled && setHover(0)}
            disabled={disabled}
          >
            <StarIcon />
          </Button>
        );
      })}
    </Box>
  );
};


const MovieDetail = () => {
  const { slug } = useParams();
  const [movie, setMovie] = useState(null);
  const [userRating, setUserRating] = useState(0);
  const [top5Kritics, setTop5Kritics] = useState([]);
  const [otherKritics, setOtherKritics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxMatchScore, setMaxMatchScore] = useState(0);

  const user = useMemo(() => JSON.parse(localStorage.getItem('user')), []);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getMovieDetailsBySlug(slug, user.id);
        setMovie(data.movie);
        setUserRating(data.movie.user_rating || 0);
        setTop5Kritics(data.top5Critics);
        setOtherKritics(data.otherCritics);
        setMaxMatchScore(data.maxMatchScore || 0);
      } catch (err) {
        setError('Failed to fetch movie details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [slug, user.id]);

  const KriticRow = ({ kritic }) => (
    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ flex: 1, minWidth: 0, marginRight: 2 }}>
        <Link component={RouterLink} to={`/kritics/${kritic.site_identifier}`} sx={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography variant="body1" noWrap>{kritic.name}</Typography>
        </Link>
        <MatchScoreIndicator score={kritic.match_score} maxScore={maxMatchScore} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', color: '#81c784', flexShrink: 0 }}>
        <StarIcon sx={{ fontSize: '1.2rem', mr: 0.5 }} />
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{kritic.normalized_score.toFixed(1)}</Typography>
      </Box>
    </ListItem>
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <EmptyState message={error} />;
  }

  if (!movie) {
    return <EmptyState message="Movie not found." />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
        <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '250px' } }}>
          <img
            src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
            alt={movie.title}
            style={{ width: '100%', borderRadius: '8px' }}
          />
        </Box>
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography variant="h3" component="h1" gutterBottom>
            {movie.title} ({new Date(movie.release_date).getFullYear()})
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {movie.overview}
          </Typography>
          <Button
            variant="outlined"
            href={`https://www.themoviedb.org/movie/${movie.tmdb_id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mb: 2 }}
          >
            View on TMDB
          </Button>
          <Box>
            <Typography variant="subtitle1" gutterBottom>Your Rating:</Typography>
            <StarRating rating={userRating} setRating={setUserRating} movieId={movie.tmdb_id} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Kritic Reviews
        </Typography>
        <RatingLegend />
      </Box>

      {top5Kritics.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: 'gold' }} /> Your Top Matched Kritics' Reviews
          </Typography>
          <Paper>
            <List>
              {top5Kritics.map((kritic, index) => (
                <React.Fragment key={kritic.site_identifier}>
                  <KriticRow kritic={kritic} />
                  {index < top5Kritics.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {otherKritics.length > 0 ? (
        <Box>
          <Typography variant="h6" component="h3" gutterBottom>
            Other Kritic Reviews
          </Typography>
          <Paper>
            <List>
              {otherKritics.map((kritic, index) => (
                <React.Fragment key={kritic.site_identifier}>
                  <KriticRow kritic={kritic} />
                  {index < otherKritics.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      ) : (
        <Typography>No kritic reviews found for this movie.</Typography>
      )}
    </Container>
  );
};

export default MovieDetail;